import { useEffect } from 'react'

import {
  useCachedSellerCollectsOrderIds,
  writeCachedOrderIds,
} from '@/buyer-portal/helpers/sellerCollectsOrderIdsCache'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import sellerCollectsOrderIdsQuery from '@/buyer-portal/queries/sellerCollectsOrderIdsQuery'

/**
 * Whether this buyer has ordered from *this seller's* Seller Collects listings
 * before.
 *
 * Per seller, not per buyer: payment and shipping instructions differ by
 * storefront, so a buyer's first order with one seller needs the explainer
 * whatever they have bought elsewhere.
 *
 * `undefined` until known, so callers can avoid claiming either state while
 * the lookup is in flight, and `null` when the count could not be fetched —
 * a settled answer of "unknown". A signed-out visitor is new to the model by
 * definition and resolves to `false` without a request.
 *
 * One lookup per session, not per page view. `staleTime: Infinity` dedupes
 * within a single React Query cache, which a client-side navigation shares but
 * a full page load does not — four listing pages opened in turn cost four
 * requests. The answer is held in `sessionStorage` instead, which survives the
 * reload and is thrown away with the session.
 *
 * `enabled: false` asks nothing at all and resolves to `undefined`: a caller
 * on a surface where the answer cannot matter — the flag is off, or the
 * listing is not Seller Collects — should not pay for a lookup per card.
 *
 * An order placed mid-session still hides the banner: the order page reads the
 * list fresh and writes it back here, so the next listing page sees it.
 */
export const useHasPriorSellerCollectsOrder = ({
  sellerAccountId,
  enabled = true,
}: {
  /** Undefined while the listing is still loading — the answer waits. */
  sellerAccountId?: string
  enabled?: boolean
} = {}): boolean | null | undefined => {
  const isLoggedIn = useIsLoggedIn()
  const { accountId, cachedOrderIds } = useCachedSellerCollectsOrderIds({
    enabled,
  })

  const { data, isError } = sellerCollectsOrderIdsQuery.useWithOpts([], {
    enabled: enabled && isLoggedIn && cachedOrderIds === null,
    staleTime: Infinity,
  })

  useEffect(() => {
    // Only a signed-in buyer's own counts are worth keeping: a stale result
    // can outlive a sign-out in the query cache, and storing it would hand the
    // next buyer in this tab somebody else's answer.
    //
    // And only when storage held nothing, which is the one case this hook
    // actually fetched. `data` survives `enabled: false` under
    // `staleTime: Infinity`, so writing it back whenever it is defined would
    // put the session-start list over a cache an order page has since folded
    // a new order into — and the banner would return for that seller.
    if (
      isLoggedIn &&
      accountId !== undefined &&
      cachedOrderIds === null &&
      data !== undefined
    ) {
      writeCachedOrderIds(accountId, data)
    }
  }, [accountId, cachedOrderIds, data, isLoggedIn])

  if (!enabled) return undefined

  if (!isLoggedIn) return false

  // No seller to ask about yet. Not `false` — that would claim a first order
  // with a seller whose identity is still loading.
  if (sellerAccountId === undefined) return undefined

  // Storage first, and not answered until it has been read. `data` is shared
  // with `useIsOnlySellerCollectsOrder`, which fetches on every order page, so
  // it can be populated here while this hook's own query is disabled — and
  // under `staleTime: Infinity` it may predate an order folded in this
  // session. Answering from it alone would call a returning buyer a
  // first-timer, show them the banner, and — worse — settle
  // `listing_viewed` with `isFirstSellerCollects: true`, which the
  // once-per-listing ref then keeps.
  if (cachedOrderIds === undefined) return undefined

  // `null` means storage held nothing, which is exactly when the query runs,
  // so the coalesce lands on its answer or stays undefined while in flight.
  const orderIds = cachedOrderIds ?? data

  if (orderIds !== undefined) {
    return (orderIds[sellerAccountId] ?? []).length > 0
  }

  // A failed count is settled at "unknown". Returning `undefined` would leave
  // a caller that waits for an answer waiting for ever.
  if (isError) return null

  return undefined
}
